import { Injectable } from '@angular/core'
import { Observable, of, shareReplay, tap } from 'rxjs'
import { AuthService } from '../auth/auth.service'

export interface CacheConfig {
    /**
     * Cache duration in milliseconds
     * @default 3600000 (1 hour)
     */
    duration?: number
}

interface CacheEntry {
    data$: Observable<any>
    timestamp: number
}

interface StoredCacheEntry {
    data: any
    timestamp: number
}

/**
 * Generic Observable caching service for HTTP responses
 * Provides time-based caching with shareReplay to optimize API calls
 * Automatically scopes cache by user ID
 */
@Injectable({
    providedIn: 'root',
})
export class ObservableCacheService {
    private readonly DEFAULT_CACHE_DURATION_MS = 60 * 60 * 1000 // 1 hour
    private readonly STORAGE_PREFIX = 'obs_cache_'
    private cache = new Map<string, CacheEntry>()

    constructor(private authService: AuthService) {}

    /**
     * Gets a cached observable or creates a new one using the factory function
     * Automatically scopes cache by current user ID
     * @param cacheKey Unique identifier for this cache entry
     * @param factory Function that creates the Observable when cache miss occurs
     * @param options Cache configuration options
     * @returns Cached or fresh Observable
     */
    public getOrSet<T>(
        cacheKey: string,
        factory: () => Observable<T>,
        options: { forceRefresh?: boolean; cacheDuration?: number } = {}
    ): Observable<T> {
        const { forceRefresh = false, cacheDuration = this.DEFAULT_CACHE_DURATION_MS } = options
        const now = Date.now()
        const userScopedKey = this.getUserScopedKey(cacheKey)
        const cachedEntry = this.cache.get(userScopedKey)

        // Check if in-memory cache is valid
        const isCacheValid = cachedEntry && !forceRefresh && now - cachedEntry.timestamp < cacheDuration

        if (isCacheValid) {
            return cachedEntry.data$ as Observable<T>
        }

        // Check sessionStorage for persisted data
        if (!forceRefresh) {
            const storedEntry = this.getFromStorage<T>(userScopedKey)
            if (storedEntry && now - storedEntry.timestamp < cacheDuration) {
                // Create Observable from stored data
                const data$ = of(storedEntry.data).pipe(shareReplay({ bufferSize: 1, refCount: false }))

                // Restore to in-memory cache
                this.cache.set(userScopedKey, { data$, timestamp: storedEntry.timestamp })

                return data$
            }
        }

        // Create new request with shareReplay and tap to store data
        const data$ = factory().pipe(
            tap((data) => this.saveToStorage(userScopedKey, data, now)),
            shareReplay({ bufferSize: 1, refCount: false })
        )

        // Store in in-memory cache with timestamp
        this.cache.set(userScopedKey, { data$, timestamp: now })

        return data$
    }

    /**
     * Gets user-scoped cache key by prepending user ID
     */
    private getUserScopedKey(cacheKey: string): string {
        const userId = this.authService.mitIdUuid || 'anonymous'
        return `${userId}:${cacheKey}`
    }

    /**
     * Saves data to sessionStorage
     */
    private saveToStorage<T>(cacheKey: string, data: T, timestamp: number): void {
        try {
            const storageKey = this.STORAGE_PREFIX + cacheKey
            const entry: StoredCacheEntry = { data, timestamp }
            sessionStorage.setItem(storageKey, JSON.stringify(entry))
        } catch (error) {
            // Silent fail if storage is full or unavailable
            console.warn('Failed to save to sessionStorage:', error)
        }
    }

    /**
     * Retrieves data from sessionStorage
     */
    private getFromStorage<T>(cacheKey: string): StoredCacheEntry | null {
        try {
            const storageKey = this.STORAGE_PREFIX + cacheKey
            const stored = sessionStorage.getItem(storageKey)
            if (stored) {
                return JSON.parse(stored) as StoredCacheEntry
            }
        } catch (error) {
            // Silent fail if parsing fails
            console.warn('Failed to read from sessionStorage:', error)
        }
        return null
    }

    /**
     * Removes data from sessionStorage
     */
    private removeFromStorage(cacheKey: string): void {
        try {
            const storageKey = this.STORAGE_PREFIX + cacheKey
            sessionStorage.removeItem(storageKey)
        } catch (error) {
            console.warn('Failed to remove from sessionStorage:', error)
        }
    }

    /**
     * Creates a composite cache key from multiple parts
     * @param parts String parts to combine into a cache key
     * @returns Composite cache key
     */
    public createKey(...parts: (string | number | boolean)[]): string {
        return parts.map((part) => String(part)).join(':')
    }

    /**
     * Clears a specific cache entry
     * @param cacheKey The cache key to clear (will be scoped to current user)
     */
    public clear(cacheKey: string): void {
        const userScopedKey = this.getUserScopedKey(cacheKey)
        this.cache.delete(userScopedKey)
        this.removeFromStorage(userScopedKey)
    }

    /**
     * Checks whether sessionStorage has an empty array for the given cache key.
     * If true, clears both in-memory and persisted cache and returns true.
     */
    public clearIfStoredValueIsEmptyArray(cacheKey: string): boolean {
        const userScopedKey = this.getUserScopedKey(cacheKey)
        const storedEntry = this.getFromStorage<any>(userScopedKey)

        if (storedEntry && Array.isArray(storedEntry.data) && storedEntry.data.length === 0) {
            this.cache.delete(userScopedKey)
            this.removeFromStorage(userScopedKey)
            return true
        }

        return false
    }

    /**
     * Clears all cache entries for the current user
     */
    public clearCurrentUserCache(): void {
        const userId = this.authService.mitIdUuid || 'anonymous'
        const userPrefix = `${userId}:`

        // Clear in-memory cache for current user
        const keysToDelete: string[] = []
        this.cache.forEach((_, key) => {
            if (key.startsWith(userPrefix)) {
                keysToDelete.push(key)
            }
        })
        keysToDelete.forEach((key) => this.cache.delete(key))

        // Clear sessionStorage for current user
        this.clearUserStorage(userId)
    }

    /**
     * Clears sessionStorage entries for a specific user
     */
    private clearUserStorage(userId: string): void {
        try {
            const userStoragePrefix = `${this.STORAGE_PREFIX}${userId}:`
            const keysToRemove: string[] = []

            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i)
                if (key && key.startsWith(userStoragePrefix)) {
                    keysToRemove.push(key)
                }
            }

            keysToRemove.forEach((key) => sessionStorage.removeItem(key))
        } catch (error) {
            console.warn('Failed to clear user sessionStorage:', error)
        }
    }

    /**
     * Clears all cache entries matching a pattern
     * @param pattern RegExp pattern to match cache keys (optional - clears all if not provided)
     */
    public clearByPattern(pattern?: RegExp): void {
        if (!pattern) {
            this.cache.clear()
            this.clearAllStorage()
            return
        }

        const keysToDelete: string[] = []
        this.cache.forEach((_, key) => {
            // Match against the cache key without the user prefix
            const keyWithoutUser = key.split(':').slice(1).join(':')
            if (pattern.test(keyWithoutUser) || pattern.test(key)) {
                keysToDelete.push(key)
            }
        })
        keysToDelete.forEach((key) => {
            this.cache.delete(key)
            this.removeFromStorage(key)
        })
    }

    /**
     * Clears all cached entries
     */
    public clearAll(): void {
        this.cache.clear()
        this.clearAllStorage()
    }

    /**
     * Clears all sessionStorage entries for this cache
     */
    private clearAllStorage(): void {
        try {
            const keysToRemove: string[] = []
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i)
                if (key && key.startsWith(this.STORAGE_PREFIX)) {
                    keysToRemove.push(key)
                }
            }
            keysToRemove.forEach((key) => sessionStorage.removeItem(key))
        } catch (error) {
            console.warn('Failed to clear sessionStorage:', error)
        }
    }

    /**
     * Gets the number of entries in the cache
     */
    public get size(): number {
        return this.cache.size
    }

    /**
     * Checks if a cache key exists (regardless of expiration)
     */
    public has(cacheKey: string): boolean {
        const userScopedKey = this.getUserScopedKey(cacheKey)
        return this.cache.has(userScopedKey)
    }

    /**
     * Clears expired cache entries
     * @param cacheDuration Optional duration to check against (uses default if not provided)
     */
    public clearExpired(cacheDuration: number = this.DEFAULT_CACHE_DURATION_MS): void {
        const now = Date.now()
        const keysToDelete: string[] = []

        // Clear expired in-memory cache
        this.cache.forEach((entry, key) => {
            if (now - entry.timestamp >= cacheDuration) {
                keysToDelete.push(key)
            }
        })

        keysToDelete.forEach((key) => {
            this.cache.delete(key)
            this.removeFromStorage(key)
        })

        // Clear expired sessionStorage entries
        this.clearExpiredStorage(cacheDuration)
    }

    /**
     * Clears expired sessionStorage entries
     */
    private clearExpiredStorage(cacheDuration: number): void {
        try {
            const now = Date.now()
            const keysToRemove: string[] = []

            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i)
                if (key && key.startsWith(this.STORAGE_PREFIX)) {
                    const stored = sessionStorage.getItem(key)
                    if (stored) {
                        try {
                            const entry: StoredCacheEntry = JSON.parse(stored)
                            if (now - entry.timestamp >= cacheDuration) {
                                keysToRemove.push(key)
                            }
                        } catch {
                            // Invalid entry, remove it
                            keysToRemove.push(key)
                        }
                    }
                }
            }

            keysToRemove.forEach((key) => sessionStorage.removeItem(key))
        } catch (error) {
            console.warn('Failed to clear expired sessionStorage:', error)
        }
    }
}
